import { UploadCloud, Zap, ShieldCheck } from 'lucide-react';

/**
 * Compact, purely-presentational mock of the public client portal, wired to
 * live form state. No data fetching, no side effects — pass the branding
 * fields in and it renders a matching preview in real time.
 */
export default function PortalBrandPreview({
    accent = '#FF6B35',
    logoUrl = null,
    brandName = null,
    welcomeMessage = null,
    canWhiteLabel = false,   // false → force Magnidrop logo/name (Pro & Starter)
    canGreeting = false,     // false → hide welcome heading (Starter)
    ownerName = 'Your studio',
    portalTitle = 'Send us your files',
    label = 'Live preview',
    testId = 'brand-preview',
}) {
    const showCustomLogo = canWhiteLabel && !!logoUrl;
    const showCustomName = canWhiteLabel && !!brandName;
    const displayName = showCustomName ? brandName : 'Magnidrop';
    const showWelcome = canGreeting && !!welcomeMessage;

    return (
        <div className="border border-border bg-background/60 backdrop-blur-sm overflow-hidden" data-testid={testId}>
            <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-card/60">
                <p className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">{label}</p>
                <p className="text-[9px] uppercase tracking-[0.22em]" style={{ color: accent }}>Client view</p>
            </div>
            {/* Portal header */}
            <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-card/40">
                <div className="flex items-center gap-2 min-w-0">
                    {showCustomLogo ? (
                        <img src={logoUrl} alt="" className="h-6 max-w-[6rem] object-contain" />
                    ) : (
                        <div className="h-6 w-6 grid place-items-center text-white shrink-0" style={{ backgroundColor: accent }}>
                            <Zap className="h-3 w-3" strokeWidth={2.5} />
                        </div>
                    )}
                    <div className="min-w-0">
                        <p className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground truncate">
                            {displayName} portal
                        </p>
                        <p className="text-xs font-medium truncate">{portalTitle}</p>
                    </div>
                </div>
                <p className="text-[9px] text-muted-foreground shrink-0">{ownerName}</p>
            </div>
            {/* Portal body */}
            <div className="p-4 space-y-3">
                {showWelcome && (
                    <div>
                        <p className="text-[9px] uppercase tracking-[0.22em] mb-1" style={{ color: accent }}>Welcome</p>
                        <p className="text-sm font-medium leading-snug" style={{ color: accent }}>
                            {welcomeMessage}
                        </p>
                    </div>
                )}
                <div
                    className="h-16 border-2 border-dashed grid place-items-center text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                    style={{ borderColor: accent }}
                >
                    <div className="flex items-center gap-2">
                        <UploadCloud className="h-3 w-3" style={{ color: accent }} />
                        Drop files
                    </div>
                </div>
                <div className="h-1.5 bg-muted overflow-hidden">
                    <div className="h-full w-2/3" style={{ backgroundColor: accent }} />
                </div>
                <button
                    className="w-full py-2 text-[10px] uppercase tracking-[0.22em] text-white"
                    style={{ backgroundColor: accent }}
                    tabIndex={-1}
                    type="button"
                >
                    Upload files
                </button>
                <div className="flex items-center gap-2 text-[9px] text-muted-foreground border-l-2 pl-2" style={{ borderColor: accent }}>
                    <ShieldCheck className="h-3 w-3" style={{ color: accent }} />
                    Secure transit · 7-day auto-purge
                </div>
            </div>
            {/* Footer watermark */}
            <div className="py-2 border-t border-border/40 text-center">
                <span className="text-[8px] uppercase tracking-[0.28em] text-muted-foreground/60">
                    Powered by Magnidrop
                </span>
            </div>
        </div>
    );
}
